import React, { useMemo } from 'react';
import { Calendar, Package } from 'lucide-react';
import { Order } from '../../../types';
import { Dialog } from '../../../components/Dialog';

interface OrderDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  orders: Order[];
}

interface CustomerOrderGroup {
  customerId: string;
  customerName: string;
  totalQuantity: number;
  unit: string;
  orders: {
    orderId: string;
    orderDate: string;
    quantity: number;
  }[];
}

export const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({
  isOpen,
  onClose,
  productId,
  productName,
  orders,
}) => {
  // Group orders by customer
  const customerGroups = useMemo(() => {
    const groups = new Map<string, CustomerOrderGroup>();

    orders.forEach(order => {
      const relevantLines = order.lines.filter(line => line.productId === productId);

      relevantLines.forEach(line => {
        if (!groups.has(order.customerId)) {
          groups.set(order.customerId, {
            customerId: order.customerId,
            customerName: order.customerName,
            totalQuantity: 0,
            unit: line.unit,
            orders: []
          });
        }

        const group = groups.get(order.customerId)!;
        group.totalQuantity += line.quantity;
        
        // Check if this order is already in the group
        const existingOrder = group.orders.find(o => o.orderId === order.id);
        if (existingOrder) {
          existingOrder.quantity += line.quantity;
        } else {
          group.orders.push({
            orderId: order.id,
            orderDate: order.orderDate,
            quantity: line.quantity
          });
        }
      });
    });

    return Array.from(groups.values()).sort((a, b) => 
      a.customerName.localeCompare(b.customerName, 'es', { sensitivity: 'base' })
    );
  }, [orders, productId]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="md" title={productName}>
      {/* Content */}
      <div className="overflow-y-auto">
        {customerGroups.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto mb-3 text-slate-300" size={48} />
            <p className="text-slate-500">No hay comandas para este producto</p>
          </div>
        ) : (
          <div className="space-y-6">
            {customerGroups.map((group) => (
              <div key={group.customerId} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                {/* Customer Header */}
                <div className="flex items-start justify-between mb-3 pb-3 border-b border-slate-200">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900">{group.customerName}</h3>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-green-700">{group.totalQuantity}</span>
                    <span className="text-lg font-semibold text-green-600">{group.unit}</span>
                  </div>
                </div>

                {/* Orders List */}
                <div className="space-y-2">
                  {group.orders.map((order, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="text-slate-400" size={16} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            Pedido #{order.orderId.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(order.orderDate).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-slate-700">{order.quantity}</span>
                        <span className="text-sm text-slate-500">{group.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-200 flex justify-between items-center">
        <div className="text-sm text-slate-600">
          <span>{customerGroups.length} cliente{customerGroups.length !== 1 ? 's' : ''}</span>
          <span className="mx-2">•</span>
          <span>
            {customerGroups.reduce((sum, g) => sum + g.orders.length, 0)} pedido
            {customerGroups.reduce((sum, g) => sum + g.orders.length, 0) !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={onClose}
          className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
        >
          Cerrar
        </button>
      </div>
    </Dialog>
  );
};
